import * as React from 'react';
import { Button, Icon, IconButton, Input } from '@vechaiui/react';
import { PlusIcon } from '@heroicons/react/solid';
import { SearchIcon } from '@heroicons/react/outline';
import { useLocalization } from '@fluent/react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import OrderCard from '#/components/cards/OrderCard';
import OrderStatusListBox from '#/components/controls/OrderStatusListBox';
import { FragmentType, getFragmentData } from '#/__generated__/graphql';
import { OrderCardFragmentDoc, OrderStatus } from '#/__generated__/graphql/graphql';
import { PrivateRoute } from '#/constants/page-route';
import { SearchParam } from '#/constants/search-params';
import { composeOrderStatusFilter } from '#/pages/OrderPage';
import { denormalizeOrderStatus } from '#/utils/order-status';
import { useManagingProfile } from '#/hooks';

const OrderListPanel: React.FC<OrderListPanelProps> = ({ orders }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const managingProfile = useManagingProfile();
  const navigate = useNavigate();
  const { l10n } = useLocalization();
  // FIXME: ugly!
  const orderStatuses = composeOrderStatusFilter(searchParams.get(SearchParam.Statuses));

  const currentKeywords = searchParams.get(SearchParam.Keywords);
  const [isSearch, setIsSearch] = React.useState(!!currentKeywords);
  const [keywords, setKeywords] = React.useState(currentKeywords || '');

  const updateSearchParams = () => {
    if (keywords !== currentKeywords) {
      setSearchParams((sp) => {
        sp.set(SearchParam.Keywords, keywords);
        sp.delete(SearchParam.OrderNumber);
        return sp;
      });
    }
  };

  const handleOrderStatusesChange = (_statues: OrderStatus[]) => {
    setSearchParams({ statuses: _statues.map(denormalizeOrderStatus).join(',') });
  };

  return (
    <div>
      <header className="border-b-1.5 border-neutral-300 relative z-20">
        <div className="flex justify-between">
          <h3 className="text-3xl font-bold">{l10n.getString('orders')}</h3>

          <div className="flex">
            {isSearch && (
              <Input
                placeholder={l10n.getString('search-by-id')}
                size="md"
                onBlur={() => {
                  updateSearchParams();
                }}
                onChange={(ev) => {
                  setKeywords(ev.target.value);
                }}
                onKeyDown={(ev) => {
                  if (ev.code === 'Enter') {
                    updateSearchParams();
                  }
                }}
                value={keywords}
              />
            )}
            <IconButton
              className="ml-5 border-gray-500 rounded-full"
              onClick={() => {
                setIsSearch(!isSearch);
              }}
            >
              <Icon as={SearchIcon} className="w-4 text-gray-500" label="selector" />
            </IconButton>
          </div>
        </div>

        <div className="mt-2">
          <div className="flex items-center justify-between py-5">
            <OrderStatusListBox
              exclusions={
                managingProfile?.__typename === 'Logistics'
                  ? [OrderStatus.OrderDraft, OrderStatus.OrderPaymentDue, OrderStatus.OrderProblem]
                  : undefined
              }
              value={orderStatuses}
              onChange={handleOrderStatusesChange}
            />
            <div>
              {orders.length > 0
                ? l10n.getString('show-order-count', {
                    count: orders.length,
                    totalCount: orders.length,
                  })
                : l10n.getString('no-orders')}
            </div>
          </div>
        </div>
      </header>

      <div className="mt-8 space-y-4">
        {orders.map((order) => (
          <OrderCard key={getFragmentData(OrderCardFragmentDoc, order).id} order={order} />
        ))}

        {orders.length === 0 && (
          <div className="mt-12">
            <div className="text-center">{l10n.getString('no-orders-message')}</div>
            {managingProfile?.__typename === 'Customer' && (
              <div className="flex justify-center mt-4">
                <Button
                  className="rounded-full"
                  color="primary"
                  leftIcon={<Icon as={PlusIcon} className="w-4 h-4 mr-2" label="add" />}
                  onClick={() => navigate(PrivateRoute.RequestNew)}
                  size="lg"
                  variant="solid"
                >
                  {l10n.getString('submit-trip-request')}
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
export type OrderListPanelProps = {
  orders: FragmentType<typeof OrderCardFragmentDoc>[];
};

export default OrderListPanel;
